/**
 * CSV Export Utility
 * Provides functionality to export data to CSV format with WCAG 2.2 AA compliance
 */

// JSZip + apiClient were used by the removed exportAllRecordsToZip helper.
// The per-entity CSV helpers below don't need either.

export interface CsvExportOptions {
  filename: string
  headers: string[]
  data: any[]
  fieldMapping?: Record<string, string | ((item: any) => string)>
}

/**
 * Escapes special characters in CSV values
 */
function escapeCSVValue(value: any): string {
  if (value === null || value === undefined) {
    return ''
  }

  const stringValue = String(value)

  // If the value contains comma, quotes, or newline, wrap in quotes and escape quotes
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`
  }

  return stringValue
}

/**
 * Formats a date value for CSV export
 */
export function formatDateForCSV(date: string | Date | null): string {
  if (!date) return ''
  const d = new Date(date)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Formats a currency value for CSV export
 */
export function formatCurrencyForCSV(value: number | string | null): string {
  if (value === null || value === undefined) return ''
  const numValue = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(numValue)) return ''
  return numValue.toFixed(2)
}

/**
 * Formats a boolean value for CSV export
 */
export function formatBooleanForCSV(value: boolean | null): string {
  if (value === null || value === undefined) return ''
  return value ? 'Yes' : 'No'
}

export const OPPORTUNITY_EXPORT_HEADERS = [
  'Opportunity Name',
  'Account',
  'Stage',
  'Source Amount',
  'Source Currency',
  'Amount (CAD)',
  'Probability',
  'Close Date',
  'Type',
  'Lead Source',
  'Owner',
  'Created Date',
]

export function mapOpportunityForCSV(opp: any): Record<string, string> {
  return {
    'Opportunity Name': opp.name || '',
    Account: opp.account_name || '',
    Stage: opp.stage_name || '',
    'Source Amount': formatCurrencyForCSV(opp.amount),
    'Source Currency': opp.currency_iso_code || '',
    'Amount (CAD)': formatCurrencyForCSV(opp.amount_cad),
    Probability: opp.probability ? `${opp.probability}%` : '',
    'Close Date': formatDateForCSV(opp.close_date),
    Type: opp.type || '',
    'Lead Source': opp.lead_source || '',
    Owner: opp.owner_name || opp.owner_id || '',
    'Created Date': formatDateForCSV(opp.created_date),
  }
}

/**
 * Builds CSV content without touching the browser download APIs.
 */
export function buildCSVContent(options: CsvExportOptions): string {
  const { headers, data, fieldMapping = {} } = options

  // Build CSV content
  const csvRows: string[] = []

  // Add headers
  csvRows.push(headers.map(escapeCSVValue).join(','))

  // Add data rows
  data.forEach((item) => {
    const row = headers.map((header) => {
      const mapping = fieldMapping[header]

      if (mapping) {
        if (typeof mapping === 'function') {
          return escapeCSVValue(mapping(item))
        } else {
          // mapping is a field name
          return escapeCSVValue(item[mapping])
        }
      }

      // Mappers may return records keyed by the display headers themselves.
      // Prefer that exact key before applying the legacy normalized-field fallback.
      if (Object.prototype.hasOwnProperty.call(item, header)) {
        return escapeCSVValue(item[header])
      }

      // Default: try to use header as field name (convert to lowercase and replace spaces with underscores)
      const fieldName = header.toLowerCase().replace(/\s+/g, '_')
      return escapeCSVValue(item[fieldName])
    })

    csvRows.push(row.join(','))
  })

  return csvRows.join('\n')
}

/**
 * Exports data to a CSV file
 */
export function exportToCSV(options: CsvExportOptions): void {
  const { filename } = options
  const csvContent = buildCSVContent(options)

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  // Create download link
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.setAttribute('aria-label', `Download ${filename} CSV file`)

  // Trigger download
  document.body.appendChild(link)
  link.click()

  // Cleanup
  document.body.removeChild(link)
  URL.revokeObjectURL(url)

  // Announce to screen readers
  const announcement = document.createElement('div')
  announcement.setAttribute('role', 'status')
  announcement.setAttribute('aria-live', 'polite')
  announcement.setAttribute('aria-atomic', 'true')
  announcement.className = 'sr-only'
  announcement.textContent = `CSV file ${filename} has been downloaded`
  document.body.appendChild(announcement)

  setTimeout(() => {
    document.body.removeChild(announcement)
  }, 3000)
}

/**
 * Hook to export leads data
 */
export function exportLeadsToCSV(leads: any[]): void {
  exportToCSV({
    filename: `leads-export-${new Date().toISOString().split('T')[0]}.csv`,
    headers: [
      'Name',
      'Company',
      'Title',
      'Email',
      'Phone',
      'Status',
      'Lead Source',
      'Rating',
      'Owner',
      'Created Date',
      'Last Modified',
    ],
    data: leads,
    fieldMapping: {
      Name: (lead) => `${lead.first_name || ''} ${lead.last_name || ''}`.trim(),
      Company: 'company',
      Title: 'title',
      Email: 'email',
      Phone: 'phone',
      Status: 'status',
      'Lead Source': 'lead_source',
      Rating: 'rating',
      Owner: (lead) => lead.owner_name || lead.owner_id || '',
      'Created Date': (lead) => formatDateForCSV(lead.created_date),
      'Last Modified': (lead) => formatDateForCSV(lead.last_modified_date),
    },
  })
}

/**
 * Hook to export contacts data
 */
export function exportContactsToCSV(contacts: any[]): void {
  exportToCSV({
    filename: `contacts-export-${new Date().toISOString().split('T')[0]}.csv`,
    headers: [
      'Name',
      'Email',
      'Phone',
      'Mobile',
      'Account',
      'Title',
      'Department',
      'Owner',
      'Created Date',
      'Last Modified',
    ],
    data: contacts,
    fieldMapping: {
      Name: (contact) => `${contact.first_name || ''} ${contact.last_name || ''}`.trim(),
      Email: 'email',
      Phone: 'phone',
      Mobile: 'mobile_phone',
      Account: 'account_name',
      Title: 'title',
      Department: 'department',
      Owner: (contact) => contact.owner_name || contact.owner_id || '',
      'Created Date': (contact) => formatDateForCSV(contact.created_date),
      'Last Modified': (contact) => formatDateForCSV(contact.last_modified_date),
    },
  })
}

/**
 * Hook to export accounts data
 */
export function exportAccountsToCSV(accounts: any[]): void {
  exportToCSV({
    filename: `accounts-export-${new Date().toISOString().split('T')[0]}.csv`,
    headers: [
      'Account Name',
      'Type',
      'Industry',
      'Annual Revenue',
      'Employees',
      'Phone',
      'Website',
      'Billing City',
      'Billing State',
      'Owner',
      'Created Date',
    ],
    data: accounts,
    fieldMapping: {
      'Account Name': 'name',
      Type: 'type',
      Industry: 'industry',
      'Annual Revenue': (account) => formatCurrencyForCSV(account.annual_revenue),
      Employees: 'number_of_employees',
      Phone: 'phone',
      Website: 'website',
      'Billing City': 'billing_city',
      'Billing State': 'billing_state',
      Owner: (account) => account.owner_name || account.owner_id || '',
      'Created Date': (account) => formatDateForCSV(account.created_date),
    },
  })
}

/**
 * Hook to export opportunities data
 */
export function exportOpportunitiesToCSV(opportunities: any[]): void {
  exportToCSV({
    filename: `opportunities-export-${new Date().toISOString().split('T')[0]}.csv`,
    headers: OPPORTUNITY_EXPORT_HEADERS,
    data: opportunities.map(mapOpportunityForCSV),
  })
}

/**
 * Hook to export tasks data
 */
export function exportTasksToCSV(tasks: any[]): void {
  exportToCSV({
    filename: `tasks-export-${new Date().toISOString().split('T')[0]}.csv`,
    headers: [
      'Subject',
      'Status',
      'Priority',
      'Due Date',
      'Related To',
      'Contact',
      'Owner',
      'Description',
      'Created Date',
      'Last Modified',
    ],
    data: tasks,
    fieldMapping: {
      Subject: 'subject',
      Status: 'status',
      Priority: 'priority',
      'Due Date': (task) => formatDateForCSV(task.activity_date),
      'Related To': (task) => task.what_name || task.what_id || '',
      Contact: (task) => task.who_name || task.who_id || '',
      Owner: (task) => task.owner_name || task.owner_id || '',
      Description: 'description',
      'Created Date': (task) => formatDateForCSV(task.created_date),
      'Last Modified': (task) => formatDateForCSV(task.last_modified_date),
    },
  })
}

/**
 * Hook to export events data
 */
export function exportEventsToCSV(events: any[]): void {
  exportToCSV({
    filename: `events-export-${new Date().toISOString().split('T')[0]}.csv`,
    headers: [
      'Subject',
      'Type',
      'Start Date',
      'End Date',
      'All Day',
      'Location',
      'Related To',
      'Contact',
      'Owner',
      'Description',
      'Created Date',
    ],
    data: events,
    fieldMapping: {
      Subject: 'subject',
      Type: 'type',
      'Start Date': (event) => formatDateForCSV(event.start_date_time),
      'End Date': (event) => formatDateForCSV(event.end_date_time),
      'All Day': (event) => formatBooleanForCSV(event.is_all_day_event),
      Location: 'location',
      'Related To': (event) => event.what_name || event.what_id || '',
      Contact: (event) => event.who_name || event.who_id || '',
      Owner: (event) => event.owner_name || event.owner_id || '',
      Description: 'description',
      'Created Date': (event) => formatDateForCSV(event.created_date),
    },
  })
}

/**
 * Hook to export products data
 */
export function exportProductsToCSV(products: any[]): void {
  exportToCSV({
    filename: `products-export-${new Date().toISOString().split('T')[0]}.csv`,
    headers: [
      'Product Name',
      'Product Code',
      'Description',
      'Status',
      'Family',
      'SKU',
      'Unit Price',
      'Currency',
      'Unit of Measure',
      'Created By',
      'Created Date',
      'Last Modified',
    ],
    data: products,
    fieldMapping: {
      'Product Name': 'name',
      'Product Code': 'product_code',
      Description: 'description',
      Status: (product) =>
        formatBooleanForCSV(product.is_active) === 'Yes' ? 'Active' : 'Inactive',
      Family: 'family',
      SKU: 'stock_keeping_unit',
      'Unit Price': (product) => formatCurrencyForCSV(product.unit_price),
      Currency: (product) => product.pricing_currency || product.currency_iso_code || '',
      'Unit of Measure': 'quantity_unit_of_measure',
      'Created By': 'created_by_name',
      'Created Date': (product) => formatDateForCSV(product.created_date),
      'Last Modified': (product) => formatDateForCSV(product.last_modified_date),
    },
  })
}

/**
 * Hook to export quotes data
 */
export function exportQuotesToCSV(quotes: any[]): void {
  exportToCSV({
    filename: `quotes-export-${new Date().toISOString().split('T')[0]}.csv`,
    headers: [
      'Quote Number',
      'Name',
      'Account',
      'Opportunity',
      'Status',
      'Total Price',
      'Discount',
      'Tax',
      'Grand Total',
      'Expiration Date',
      'Owner',
      'Created Date',
    ],
    data: quotes,
    fieldMapping: {
      'Quote Number': 'quote_number',
      Name: 'name',
      Account: 'account_name',
      Opportunity: 'opportunity_name',
      Status: 'status',
      'Total Price': (quote) => formatCurrencyForCSV(quote.total_price),
      Discount: (quote) => (quote.discount ? `${quote.discount}%` : '0%'),
      Tax: (quote) => (quote.tax ? `${quote.tax}%` : '0%'),
      'Grand Total': (quote) => formatCurrencyForCSV(quote.grand_total),
      'Expiration Date': (quote) => formatDateForCSV(quote.expiration_date),
      Owner: (quote) => quote.owner_name || quote.owner_id || '',
      'Created Date': (quote) => formatDateForCSV(quote.created_date),
    },
  })
}

/**
 * Hook to export users data
 */
export function exportUsersToCSV(users: any[]): void {
  exportToCSV({
    filename: `users-export-${new Date().toISOString().split('T')[0]}.csv`,
    headers: [
      'Name',
      'Email',
      'Username',
      'User Type',
      'Status',
      'Department',
      'Manager',
      'Accounts Owned',
      'Opportunities Owned',
      'Tasks Owned',
      'Last Login',
      'Created Date',
      'Last Modified',
    ],
    data: users,
    fieldMapping: {
      Name: 'name',
      Email: 'email',
      Username: 'username',
      'User Type': 'user_type',
      Status: (user) => (formatBooleanForCSV(user.is_active) === 'Yes' ? 'Active' : 'Inactive'),
      Department: 'department',
      Manager: 'manager_name',
      'Accounts Owned': (user) => user.accounts_owned || 0,
      'Opportunities Owned': (user) => user.opportunities_owned || 0,
      'Tasks Owned': (user) => user.tasks_owned || 0,
      'Last Login': (user) => formatDateForCSV(user.last_login),
      'Created Date': (user) => formatDateForCSV(user.created_date),
      'Last Modified': (user) => formatDateForCSV(user.last_modified_date),
    },
  })
}

/**
 * Hook to export emails data
 */
export function exportEmailsToCSV(emails: any[]): void {
  exportToCSV({
    filename: `emails-export-${new Date().toISOString().split('T')[0]}.csv`,
    headers: [
      'Subject',
      'From',
      'To',
      'CC',
      'BCC',
      'Status',
      'Direction',
      'Has Attachment',
      'Related To',
      'Message Date',
      'Created Date',
      'Owner',
    ],
    data: emails,
    fieldMapping: {
      Subject: 'subject',
      From: 'from_address',
      To: 'to_address',
      CC: 'cc_address',
      BCC: 'bcc_address',
      Status: 'status',
      Direction: (email) => (email.incoming ? 'Incoming' : 'Outgoing'),
      'Has Attachment': (email) => formatBooleanForCSV(email.has_attachment),
      'Related To': (email) => {
        if (email.related_to_name) return email.related_to_name
        if (email.what_name) return email.what_name
        if (email.what_id) return email.what_id
        return ''
      },
      'Message Date': (email) => formatDateForCSV(email.message_date),
      'Created Date': (email) => formatDateForCSV(email.created_date),
      Owner: (email) => email.owner_name || email.owner_id || '',
    },
  })
}
